import { useCallback, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
  type StripeElementChangeEvent,
  type StripeElementStyle,
} from '@stripe/stripe-js'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
} from '@stripe/react-stripe-js'
import {
  InputShellControl,
  InputShellRoot,
  InputShellSlot,
} from '@/components/inputs/InputShell'
import { Icon } from '@/components/Icon'
import { useIsSkeleton } from '@/components/Skeleton'
import { useCssVariable } from '@/features/shell/theming/use-css-variable'

interface CardFieldProps {
  disabled?: boolean
  id?: string
  invalid?: boolean
  onChange?: ElementChangeEventHandler
  onReady?: () => unknown
  testId?: string
}

/**
 * Change event handler for Stripe Elements.
 */
type ElementChangeEventHandler = (event: StripeElementChangeEvent) => unknown

/**
 * Card number field.
 */
export const CardNumberField = observer(function CardNumberField({
  disabled,
  id,
  onChange,
  invalid,
  onReady,
  testId,
}: CardFieldProps) {
  const { focusProps, isFocused, focus } = useElementFocus('cardNumber')
  const isSkeleton = useIsSkeleton()
  const style = useStripeElementStyle()

  return (
    <InputShellRoot
      isTextField
      isSkeleton={isSkeleton}
      isDisabled={disabled}
      isFocused={isFocused}
      invalid={invalid}
      onPointerDown={focus}
      testId={testId}
    >
      <InputShellControl pad>
        <CardNumberElement
          id={id}
          onChange={onChange}
          onReady={onReady}
          {...focusProps}
          options={{
            disabled,
            style,
          }}
        />
      </InputShellControl>
      <InputShellSlot side="right">
        <Icon variant="secondary" name="card-number" />
      </InputShellSlot>
    </InputShellRoot>
  )
})

/**
 * Card expiry field.
 */
export const CardExpiryField = observer(function CardExpiryField({
  disabled,
  id,
  onChange,
  invalid,
  onReady,
  testId,
}: CardFieldProps) {
  const { focusProps, isFocused, focus } = useElementFocus('cardExpiry')
  const isSkeleton = useIsSkeleton()
  const style = useStripeElementStyle()

  return (
    <InputShellRoot
      isTextField
      isSkeleton={isSkeleton}
      isDisabled={disabled}
      isFocused={isFocused}
      invalid={invalid}
      onPointerDown={focus}
      testId={testId}
    >
      <InputShellControl pad>
        <CardExpiryElement
          id={id}
          onChange={onChange}
          onReady={onReady}
          {...focusProps}
          options={{
            disabled,
            style,
          }}
        />
      </InputShellControl>
    </InputShellRoot>
  )
})

/**
 * Card CVC field.
 */
export const CardCvcField = observer(function CardCvcField({
  disabled,
  id,
  onChange,
  invalid,
  onReady,
  testId,
}: CardFieldProps) {
  const { focusProps, isFocused, focus } = useElementFocus('cardCvc')
  const isSkeleton = useIsSkeleton()
  const style = useStripeElementStyle()

  return (
    <InputShellRoot
      isTextField
      isSkeleton={isSkeleton}
      isDisabled={disabled}
      isFocused={isFocused}
      invalid={invalid}
      onPointerDown={focus}
      testId={testId}
    >
      <InputShellControl pad>
        <CardCvcElement
          id={id}
          onChange={onChange}
          onReady={onReady}
          {...focusProps}
          options={{
            disabled,
            style,
          }}
        />
      </InputShellControl>
      <InputShellSlot side="right">
        <Icon variant="secondary" name="card-cvv" />
      </InputShellSlot>
    </InputShellRoot>
  )
})

/**
 * Tracks focus of a Stripe Element.
 */
function useElementFocus(elementType: 'cardNumber' | 'cardCvc' | 'cardExpiry') {
  const elements = useElements()
  const [isFocused, setFocused] = useState(false)
  const onFocus = useCallback(() => {
    setFocused(true)
  }, [])
  const onBlur = useCallback(() => {
    setFocused(false)
  }, [])

  const focus = useCallback(() => {
    // @ts-ignore
    elements?.getElement(elementType)?.focus()
  }, [elementType, elements])

  return {
    isFocused,
    focus,
    focusProps: {
      onFocus: onFocus,
      onBlur: onBlur,
    },
  }
}

/**
 * Creates a style object for a Stripe element based on resolved
 * CSS variables.
 */
function useStripeElementStyle(): StripeElementStyle {
  return {
    base: {
      color: useCssVariable('--font-color-input-active'),
      fontFamily: useCssVariable('--font-family-primary'),
      fontSize: '14px', // This is actually `--font-size-base` but the `rem` unit causes weird layout shift
      fontWeight: useCssVariable('--font-weight-regular'),
    },
    invalid: {
      color: useCssVariable('--font-color-input-danger'),
    },
    empty: {
      '::placeholder': {
        color: useCssVariable('--font-color-input-placeholder'),
        fontStyle: 'italic',
        fontWeight: useCssVariable('--font-weight-light'),
      },
    },
  }
}
